<template>
  <Dialog :open="isOpen" @update:open="updateOpen">
    <DialogContent class="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <Settings class="h-5 w-5" />
          App Settings
        </DialogTitle>
        <DialogDescription>
          Configure your application settings and preferences.
        </DialogDescription>
      </DialogHeader>
      
      <div class="space-y-6 py-4">
        <!-- App Information Section -->
        <div class="space-y-4">
          <h3 class="text-lg font-semibold">App Information</h3>
          <div class="grid grid-cols-1 gap-4">
            <div class="space-y-2">
              <Label for="app-name">App Name</Label>
              <Input
                id="app-name"
                v-model="settings.appName"
                placeholder="Enter your app name"
              />
            </div>
            <div class="space-y-2">
              <Label for="app-description">Description</Label>
              <Textarea
                id="app-description"
                v-model="settings.description"
                placeholder="Describe your app"
                rows="3"
              />
            </div>
          </div>
        </div>

        <!-- Theme Settings -->
        <div class="space-y-4">
          <h3 class="text-lg font-semibold">Theme Settings</h3>
          <div class="space-y-4">
            <div class="space-y-2">
              <Label>Color Scheme</Label>
              <div class="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  @click="settings.theme = 'light'"
                  :class="{ 'bg-primary text-primary-foreground': settings.theme === 'light' }"
                >
                  Light
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  @click="settings.theme = 'dark'"
                  :class="{ 'bg-primary text-primary-foreground': settings.theme === 'dark' }"
                >
                  Dark
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  @click="settings.theme = 'system'"
                  :class="{ 'bg-primary text-primary-foreground': settings.theme === 'system' }"
                >
                  System
                </Button>
              </div>
            </div>
            
            <div class="space-y-2">
              <Label>Color Palette</Label>
              <div class="grid grid-cols-1 gap-3">
                <div 
                  v-for="palette in slidesStore.colorPalettes" 
                  :key="palette.id"
                  class="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                  :class="{ 'border-primary bg-primary/5': slidesStore.currentColorPalette === palette.id }"
                  @click="selectColorPalette(palette.id)"
                >
                  <div class="flex items-center space-x-3">
                    <div class="flex space-x-1">
                      <div 
                        class="w-4 h-4 rounded-full border border-gray-200" 
                        :style="{ backgroundColor: palette.primary }"
                      ></div>
                      <div 
                        class="w-4 h-4 rounded-full border border-gray-200" 
                        :style="{ backgroundColor: palette.secondary }"
                      ></div>
                      <div 
                        class="w-4 h-4 rounded-full border border-gray-200" 
                        :style="{ backgroundColor: palette.accent }"
                      ></div>
                    </div>
                    <span class="font-medium">{{ palette.name }}</span>
                  </div>
                  <div 
                    v-if="slidesStore.currentColorPalette === palette.id"
                    class="w-5 h-5 rounded-full bg-primary flex items-center justify-center"
                  >
                    <div class="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Feature Toggles -->
        <div class="space-y-4">
          <h3 class="text-lg font-semibold">Features</h3>
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <div class="space-y-0.5">
                <Label>Enable Notifications</Label>
                <p class="text-sm text-muted-foreground">
                  Receive app notifications and updates
                </p>
              </div>
              <Switch
                v-model="settings.notifications"
              />
            </div>
            <div class="flex items-center justify-between">
              <div class="space-y-0.5">
                <Label>Analytics</Label>
                <p class="text-sm text-muted-foreground">
                  Help improve the app by sharing usage data
                </p>
              </div>
              <Switch
                v-model="settings.analytics"
              />
            </div>
            <div class="flex items-center justify-between">
              <div class="space-y-0.5">
                <Label>Auto-save</Label>
                <p class="text-sm text-muted-foreground">
                  Automatically save your work
                </p>
              </div>
              <Switch
                v-model="settings.autoSave"
              />
            </div>
          </div>
        </div>

        <!-- API Configuration -->
        <div class="space-y-4">
          <h3 class="text-lg font-semibold">API Configuration</h3>
          <div class="space-y-2">
            <Label for="api-endpoint">API Endpoint</Label>
            <Input
              id="api-endpoint"
              v-model="settings.apiEndpoint"
              placeholder="https://api.example.com"
            />
          </div>
        </div>
      </div>

      <DialogFooter class="gap-2">
        <Button variant="outline" @click="closeModal">
          Cancel
        </Button>
        <Button @click="saveSettings" variant="default">
          Save Settings
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Settings } from 'lucide-vue-next'
import { useToast } from '@/components/ui/toast/use-toast'
import { useSlidesStore } from '../stores/slides'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:isOpen'])

const { toast } = useToast()
const slidesStore = useSlidesStore()

// Default settings that can be customized for any app
const settings = ref({
  appName: 'My App',
  description: 'A modern web application built with Vue and Nuxt',
  theme: 'system',
  notifications: true,
  analytics: false,
  autoSave: true,
  apiEndpoint: 'https://api.example.com'
})

const updateOpen = (open) => {
  emit('update:isOpen', open)
}

const closeModal = () => {
  emit('update:isOpen', false)
}

const selectColorPalette = (paletteId) => {
  slidesStore.setColorPalette(paletteId)
  
  toast({
    title: "Color palette updated",
    description: `Switched to ${slidesStore.getCurrentPalette()?.name} palette.`,
  })
}

const saveSettings = () => {
  // Here you would typically save to localStorage, database, or state management
  localStorage.setItem('appSettings', JSON.stringify(settings.value))
  
  toast({
    title: "Settings saved",
    description: "Your app settings have been updated successfully.",
  })
  
  closeModal()
}

// Load settings from localStorage on component mount
const loadSettings = () => {
  const saved = localStorage.getItem('appSettings')
  if (saved) {
    try {
      const parsed = JSON.parse(saved)
      settings.value = { ...settings.value, ...parsed }
    } catch (e) {
      console.error('Failed to load settings:', e)
    }
  }
}

// Load settings when component mounts
loadSettings()
</script>
